import React, { useMemo } from "react"
import Select from "@mui/material/Select"
import { makeStyles } from "tss-react/mui"
import { ListSubheader } from "@mui/material"
import MenuItem from "@mui/material/MenuItem"
import Checkbox from "@mui/material/Checkbox"
import InputLabel from "@mui/material/InputLabel"
import FormControl from "@mui/material/FormControl"
import ListItemText from "@mui/material/ListItemText"
import OutlinedInput from "@mui/material/OutlinedInput"
import { LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"
import { useSupplier } from "../../../../modules/supplier/supplierHooks"
import {
    fetchSupplierShopsList,
    SupplierShopQuery,
    SupplierShopsListResponse,
} from "../../../../modules/supplier/reporting/accounting/shop/shopApi"
import { useApiQuery } from "../../../../core/api/useApiQuery"
import { MenuProps } from "../constants/filterMenuConst"

type StandardMultiMerchantShopsFilterProps = Readonly<{
    selectedIds: string[]
    setSelectedIds: (value: string[]) => void
    merchantIds?: string[]
}>

const useStyles = makeStyles()(() => ({
    input: {
        width: "20rem",
    },
    listItem: {
        textWrap: "auto",
    },
}))

function transformData(data: SupplierShopsListResponse) {
    return data.merchants
}

export function StandardMultiMerchantShopsFilter({
    merchantIds,
    selectedIds,
    setSelectedIds,
}: StandardMultiMerchantShopsFilterProps) {
    const { classes } = useStyles()

    const { supplierId } = useSupplier()

    const { data } = useApiQuery(fetchSupplierShopsList, {
        queryName: SupplierShopQuery.SHOPS_LIST,
        dependencies: {
            supplierId,
            merchantIds: merchantIds?.join(",") ?? "",
        },
        enabled: Boolean(merchantIds?.length),
        select: transformData,
    })

    const shopMap = useMemo(() => {
        const map: { [id: string]: { name: string; merchantName: string } } = {}
        data?.forEach((merchant) =>
            merchant.shops.forEach((shop) => {
                map[shop.id] = { name: shop.name, merchantName: merchant.name }
            }),
        )
        return map
    }, [data])

    const handleMenuItemClick = (shopId: string) => {
        const selectedIndex = selectedIds.indexOf(shopId)
        let newSelectedIds: string[] = []

        if (selectedIndex === -1) {
            newSelectedIds = [...selectedIds, shopId]
        } else {
            newSelectedIds = selectedIds.filter((id) => id !== shopId)
        }

        setSelectedIds(newSelectedIds)
    }

    return (
        <FormControl className={classes.input}>
            <LocalizedStrict id="standard-merchants-shops-filter-label">
                <InputLabel id="multiple-checkbox-shops-label">Shops</InputLabel>
            </LocalizedStrict>
            <Select
                labelId="multiple-checkbox-shops-label"
                id="multiple-checkbox-shops"
                multiple
                disabled={!Boolean(merchantIds?.length) || !data}
                value={selectedIds}
                input={<OutlinedInput label="Shops" />}
                renderValue={(selected) =>
                    selected
                        .map((shopId) => `${shopMap[shopId].name} (${shopMap[shopId].merchantName})` || "")
                        .join(", ")
                }
                MenuProps={MenuProps}
            >
                {data?.map((merchant) => (
                    <React.Fragment key={`${merchant.name}-header`}>
                        <ListSubheader>{merchant.name}</ListSubheader>
                        {merchant.shops.map((shop) => (
                            <MenuItem
                                key={shop.id}
                                value={shop.id}
                                onClick={() => handleMenuItemClick(shop.id.toString())}
                            >
                                <Checkbox checked={selectedIds.indexOf(shop.id.toString()) > -1} />
                                <ListItemText className={classes.listItem} primary={shop.name} />
                            </MenuItem>
                        ))}
                    </React.Fragment>
                ))}
            </Select>
        </FormControl>
    )
}
